import { useEffect, useMemo, useState } from "react";
import { useRecoilValue } from "recoil";
import { Button, Input, Tooltip, message } from "antd";
import { DeleteOutlined, DownOutlined, LinkOutlined, SearchOutlined } from "@ant-design/icons";
import { memberState } from "../../state/atoms";
import { LocationDto } from "../../generated/dashboard-client";
import { useBrandDestination } from "../../service/useBrandDestination";
import { ChoosePhotoModal, ChosenPhoto } from "./ChoosePhotoModal";
import { DeletePhotoModal } from "./DeletePhotoModal";
import YextApi, { ensureHttps, yextErrorMessage, ListingPhoto, ListingPhotos, YextCategory, YextProfile, YextSyncStatus } from "../../api/YextApi";
import "./ListingsProfileTab.scss";

const { TextArea } = Input;

const NO_PHOTOS: ListingPhotos = { gallery: [] };

// Where a photo picked in the Choose Photo modal lands: the two singleton slots or the gallery.
type PhotoTarget = "COVER" | "LOGO" | "GALLERY";

type YesNo = "yes" | "no" | null;

const QUICK_TYPES = [
  "Pizza Restaurant",
  "Mexican Restaurant",
  "American Restaurant",
  "Cafe",
  "Coffee Shop",
  "Burger Joint",
  "Bakery",
  "Bar & Grill",
  "Chinese Restaurant",
  "Italian Restaurant",
  "Breakfast Restaurant",
  "Sushi Restaurant",
];

const MAX_CATEGORIES = 5;

// Price is displayed as $..$$$$ but the 4th value stays "$$$$+" to match the backend's toPriceRange.
const PRICE_OPTIONS: { value: string; label: string }[] = [
  { value: "$", label: "$" },
  { value: "$$", label: "$$" },
  { value: "$$$", label: "$$$" },
  { value: "$$$$+", label: "$$$$" },
];

const PAYMENT_OPTIONS = ["Cards", "Mobile Pay", "Cash"];

// Legacy amenity tokens, still read back from profiles saved before the yes/no fields existed.
const TAKEOUT = "Takeout";
const DELIVERY = "Delivery";
const WHEELCHAIR = "Wheelchair-accessible entrance";

// Optional extras revealed from the "Add to your profile" chips.
type ExtraKey =
  | "alsoKnownAs"
  | "languagesSpoken"
  | "faqs"
  | "featuredMessage"
  | "blackOwned"
  | "xHandle"
  | "video";

const EXTRA_CHIPS: { key: ExtraKey; label: string }[] = [
  { key: "alsoKnownAs", label: "Also known as" },
  { key: "languagesSpoken", label: "Languages spoken" },
  { key: "faqs", label: "FAQs" },
  { key: "featuredMessage", label: "Featured message" },
  { key: "blackOwned", label: "Black-owned business" },
  { key: "xHandle", label: "X handle" },
  { key: "video", label: "Video" },
];

interface ProfileForm {
  // Basics
  businessName: string;
  street: string;
  street2: string;
  city: string;
  state: string;
  zip: string;
  primaryPhone: string;
  website: string;
  wayfindingNote: string; // -> displayAddress
  // Category
  categories: string[];
  // Action links
  onlineOrdering: string;
  reservations: string;
  deliveryLink: string;
  // Quick questions
  acceptsReservations: YesNo;
  outdoorSeating: YesNo;
  takeout: YesNo;
  delivery: YesNo;
  wheelchair: YesNo;
  priceRange: string | null;
  paymentAccepted: string[];
  // Amenities
  cuisineTypes: string;
  alcoholServed: string[];
  wifiAvailable: YesNo;
  // About
  description: string; // -> descriptionLong
  yearEstablished: string; // -> openingDate (YYYY-01-01)
  instagram: string;
  facebook: string;
  // Add to profile (optional)
  alsoKnownAs: string; // -> alternateNames
  languagesSpoken: string;
  faqs: string;
  featuredMessage: string;
  blackOwned: YesNo;
  xHandle: string; // -> social.twitter
  video: string;
}

const EMPTY: ProfileForm = {
  businessName: "",
  street: "",
  street2: "",
  city: "",
  state: "",
  zip: "",
  primaryPhone: "",
  website: "",
  wayfindingNote: "",
  categories: [],
  onlineOrdering: "",
  reservations: "",
  deliveryLink: "",
  acceptsReservations: null,
  outdoorSeating: null,
  takeout: null,
  delivery: null,
  wheelchair: null,
  priceRange: null,
  paymentAccepted: [],
  cuisineTypes: "",
  alcoholServed: [],
  wifiAvailable: null,
  description: "",
  yearEstablished: "",
  instagram: "",
  facebook: "",
  alsoKnownAs: "",
  languagesSpoken: "",
  faqs: "",
  featuredMessage: "",
  blackOwned: null,
  xHandle: "",
  video: "",
};

const NONE_OPTION = "None";
const ALCOHOL_OPTIONS = ["Beer", "Wine", "Cocktails", NONE_OPTION];

function withNoneExclusivity(values: string[]) {
  const selected = values.filter((v) => v !== NONE_OPTION);
  return selected.length ? selected : values.filter((v) => v === NONE_OPTION);
}

interface Props {
  locationId?: number;
  onDirtyChange?: (dirty: boolean) => void;
  syncStatus?: YextSyncStatus | null;
  onSyncChange?: () => void;
  publishLabel?: string;
  showSaveChanges?: boolean;
  onPublished?: () => void;
}

export function ListingsProfileTab({
  locationId,
  onDirtyChange,
  syncStatus,
  onSyncChange,
  publishLabel = "Publish Changes",
  showSaveChanges = false,
  onPublished,
}: Props) {
  const [form, setForm] = useState<ProfileForm>(EMPTY);
  const [savedSnapshot, setSavedSnapshot] = useState<ProfileForm>(EMPTY);
  const [saving, setSaving] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [gallery, setGallery] = useState<string[]>([]);
  const [wayfindingOpen, setWayfindingOpen] = useState(false);
  const [catQuery, setCatQuery] = useState("");
  const [catalog, setCatalog] = useState<YextCategory[]>([]);
  const [revealed, setRevealed] = useState<Set<ExtraKey>>(new Set());
  const [photos, setPhotos] = useState<ListingPhotos>(NO_PHOTOS);
  const [choosing, setChoosing] = useState<PhotoTarget | null>(null);
  const [pendingDelete, setPendingDelete] = useState<ListingPhoto | null>(null);

  const member = useRecoilValue(memberState);
  const { destinationFor, goToBrand } = useBrandDestination();
  const brandLocation = useMemo(() => ({ internalId: locationId } as LocationDto), [locationId]);

  const dirty = JSON.stringify(form) !== JSON.stringify(savedSnapshot);
  useEffect(() => {
    onDirtyChange?.(dirty);
    return () => onDirtyChange?.(false);
  }, [dirty, onDirtyChange]);

  useEffect(() => {
    YextApi.listCategories()
      .then(({ data }) => setCatalog(data))
      .catch(() => setCatalog([]));
  }, []);

  useEffect(() => {
    if (!catalog.length) return;
    const idByName = new Map(catalog.map((c) => [c.name.toLowerCase(), c.id]));
    const ids = new Set(catalog.map((c) => c.id));
    const toIds = (values: string[]) =>
      values.map((v) => (ids.has(v) ? v : idByName.get(v.toLowerCase()))).filter((v): v is string => !!v);
    const swap = (f: ProfileForm) => {
      const next = toIds(f.categories);
      return next.join() === f.categories.join() ? f : { ...f, categories: next };
    };
    setForm(swap);
    setSavedSnapshot(swap);
  }, [catalog]);

  useEffect(() => {
    if (!locationId) return;
    YextApi.getProfile(locationId).then(({ data }) => {
      const a = data.amenities;
      const social = data.links?.social ?? {};
      const ordering = data.links?.onlineOrdering ?? [];
      const yearFromOpening = data.openingDate ? data.openingDate.slice(0, 4) : "";
      const loaded: ProfileForm = {
        businessName: data.businessName ?? "",
        street: data.street ?? "",
        street2: data.street2 ?? "",
        city: data.city ?? "",
        state: data.state ?? "",
        zip: data.zip ?? "",
        primaryPhone: data.primaryPhone ?? "",
        website: data.website ?? "",
        wayfindingNote: data.displayAddress ?? "",
        categories: data.category ?? [],
        onlineOrdering: ordering[0] ?? "",
        reservations: data.links?.reservations?.[0] ?? "",
        deliveryLink: data.links?.delivery?.[0] ?? ordering[1] ?? "",
        acceptsReservations: (a?.reservationSupport as YesNo) ?? null,
        outdoorSeating: (a?.outdoorSeating as YesNo) ?? null,
        takeout: (a?.takeout as YesNo) ?? (a?.serviceOptions?.includes(TAKEOUT) ? "yes" : null),
        delivery: (a?.delivery as YesNo) ?? (a?.serviceOptions?.includes(DELIVERY) ? "yes" : null),
        wheelchair: (a?.wheelchairAccessible as YesNo) ?? (a?.accessibility?.includes(WHEELCHAIR) ? "yes" : null),
        priceRange: a?.priceRange ?? null,
        paymentAccepted: a?.paymentAccepted ?? [],
        cuisineTypes: a?.cuisineTypes ?? "",
        alcoholServed: withNoneExclusivity(a?.alcoholServed ?? []),
        wifiAvailable: (a?.wifiAvailable as YesNo) ?? null,
        description: data.descriptionLong ?? "",
        yearEstablished: yearFromOpening,
        instagram: social.instagram ?? "",
        facebook: social.facebook ?? "",
        alsoKnownAs: data.alternateNames ?? "",
        languagesSpoken: (a?.languages ?? []).join(", "),
        faqs: "",
        featuredMessage: data.featuredMessage ?? "",
        blackOwned: (a?.blackOwned as YesNo) ?? null,
        xHandle: social.twitter ?? "",
        video: data.video ?? "",
      };
      setForm(loaded);
      setSavedSnapshot(data.existing ? loaded : EMPTY);
      setGallery(data.visualAssets?.photoGallery ?? []);
      if (loaded.wayfindingNote) setWayfindingOpen(true);
      // Reveal any optional extras that already carry a value.
      const toReveal = new Set<ExtraKey>();
      if (loaded.alsoKnownAs) toReveal.add("alsoKnownAs");
      if (loaded.featuredMessage) toReveal.add("featuredMessage");
      if (loaded.xHandle) toReveal.add("xHandle");
      if (loaded.languagesSpoken) toReveal.add("languagesSpoken");
      if (loaded.blackOwned) toReveal.add("blackOwned");
      if (loaded.video) toReveal.add("video");
      setRevealed(toReveal);
    });
  }, [locationId]);

  useEffect(() => {
    if (!locationId) return;
    YextApi.getPhotos(locationId)
      .then(({ data }) => setPhotos(data))
      .catch(() => setPhotos(NO_PHOTOS));
  }, [locationId]);

  function update<K extends keyof ProfileForm>(key: K, value: ProfileForm[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  function toggleCategory(id: string) {
    setForm((f) => {
      if (f.categories.includes(id)) {
        return { ...f, categories: f.categories.filter((c) => c !== id) };
      }
      if (f.categories.length >= MAX_CATEGORIES) return f;
      return { ...f, categories: [...f.categories, id] };
    });
  }

  function toggleMulti(key: "alcoholServed" | "paymentAccepted", value: string) {
    setForm((f) => {
      const next = f[key].includes(value) ? f[key].filter((v) => v !== value) : [...f[key], value];
      const keep = value === NONE_OPTION ? (v: string) => v === NONE_OPTION : (v: string) => v !== NONE_OPTION;
      return { ...f, [key]: next.filter(keep) };
    });
  }

  function reveal(key: ExtraKey) {
    setRevealed((r) => new Set(r).add(key));
  }

  function reloadVisualAssets() {
    if (!locationId) return;
    YextApi.getProfile(locationId).then(({ data }) => {
      setGallery(data.visualAssets?.photoGallery ?? []);
    });
  }

  function insertPhoto(photo: ChosenPhoto) {
    const target = choosing;
    if (!target || !locationId) return;
    const saved =
      target === "GALLERY"
        ? YextApi.addGalleryPhoto(locationId, photo).then(({ data }) =>
            setPhotos((prev) => ({ ...prev, gallery: [...prev.gallery, data] })),
          )
        : YextApi.setPhoto(locationId, target, photo).then(({ data }) =>
            setPhotos((prev) => ({ ...prev, [target === "COVER" ? "cover" : "logo"]: data })),
          );
    saved
      .then(() => {
        setChoosing(null);
        reloadVisualAssets();
        onSyncChange?.();
      })
      .catch(() => message.error("Failed to add photo"));
  }

  function removeGalleryPhoto(photo: ListingPhoto) {
    if (!locationId || photo.id == null) return;
    YextApi.deleteGalleryPhoto(locationId, photo.id)
      .then(() => {
        setPhotos((prev) => ({ ...prev, gallery: prev.gallery.filter((p) => p.id !== photo.id) }));
        setPendingDelete(null);
        reloadVisualAssets();
        onSyncChange?.();
      })
      .catch(() => message.error("Failed to remove photo"));
  }

  const searchResults = useMemo(() => {
    const q = catQuery.trim().toLowerCase();
    if (!q) return [];
    return catalog.filter((c) => c.name.toLowerCase().includes(q) && !form.categories.includes(c.id)).slice(0, 8);
  }, [catQuery, catalog, form.categories]);

  const quickCategories = useMemo(() => {
    const byName = new Map(catalog.map((c) => [c.name.toLowerCase(), c]));
    return QUICK_TYPES.map((name) => byName.get(name.toLowerCase())).filter((c): c is YextCategory => !!c);
  }, [catalog]);

  const categoryById = useMemo(() => new Map(catalog.map((c) => [c.id, c])), [catalog]);

  const extraSelected = form.categories.filter((id) => !quickCategories.some((c) => c.id === id));

  const strength = useMemo(() => computeStrength(form, gallery), [form, gallery]);

  // The wire slice sent for both draft autosave and publish.
  function buildProfileSlice(): YextProfile {
    return {
      businessName: form.businessName,
      street: form.street,
      street2: form.street2,
      city: form.city,
      state: form.state,
      zip: form.zip,
      primaryPhone: form.primaryPhone,
      website: form.website,
      displayAddress: form.wayfindingNote,
      category: form.categories,
      alternateNames: form.alsoKnownAs,
      featuredMessage: form.featuredMessage,
      video: form.video,
      descriptionLong: form.description,
      openingDate: form.yearEstablished ? `${form.yearEstablished}-01-01` : undefined,
      links: {
        onlineOrdering: [form.onlineOrdering].filter(Boolean),
        delivery: [form.deliveryLink].filter(Boolean),
        reservations: [form.reservations].filter(Boolean),
        social: { facebook: form.facebook, instagram: form.instagram, twitter: form.xHandle },
      },
      amenities: {
        reservationSupport: form.acceptsReservations ?? undefined,
        outdoorSeating: form.outdoorSeating ?? undefined,
        wifiAvailable: form.wifiAvailable ?? undefined,
        takeout: form.takeout ?? undefined,
        delivery: form.delivery ?? undefined,
        wheelchairAccessible: form.wheelchair ?? undefined,
        paymentAccepted: form.paymentAccepted,
        alcoholServed: form.alcoholServed,
        cuisineTypes: form.cuisineTypes,
        priceRange: form.priceRange ?? undefined,
        languages: form.languagesSpoken
          .split(",")
          .map((l) => l.trim())
          .filter(Boolean),
        blackOwned: form.blackOwned ?? undefined,
      },
    };
  }

  // Autosave the draft to our DB (no Yext) shortly after edits settle. `dirty` is form-vs-last-saved.
  useEffect(() => {
    if (!locationId || !dirty) return;
    const snapshot = form;
    const t = setTimeout(() => {
      YextApi.saveDraft(locationId, buildProfileSlice())
        .then(() => {
          setSavedSnapshot(snapshot);
          onSyncChange?.();
        })
        .catch(() => {
          // Best-effort; the edit stays in the form and Publish still works.
        });
    }, 1000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dirty, form, locationId]);

  // Publish is available when the draft is ahead of live (locally unsaved, or per the backend).
  const canPublish = dirty || (syncStatus?.hasUnpublishedChanges ?? false);

  async function saveDraftNow() {
    if (!locationId) {
      message.error("No location selected.");
      return;
    }
    const snapshot = form;
    setSavingDraft(true);
    try {
      await YextApi.saveDraft(locationId, buildProfileSlice());
      setSavedSnapshot(snapshot);
      message.success("Changes saved.");
      onSyncChange?.();
    } catch (e) {
      message.error(yextErrorMessage(e, "Failed to save your changes."));
    } finally {
      setSavingDraft(false);
    }
  }

  async function publishChanges() {
    if (!locationId) {
      message.error("No location selected.");
      return;
    }
    setSaving(true);
    try {
      await YextApi.syncLocation(locationId, buildProfileSlice());
      setSavedSnapshot(form);
      message.success("Profile published.");
      onPublished?.();
    } catch (e) {
      message.error(yextErrorMessage(e, "Failed to publish profile."));
    } finally {
      setSaving(false);
      // Refresh the pill either way — success flips it to "Synced", a failure surfaces "Sync failed".
      onSyncChange?.();
    }
  }

  return (
    <div className="listings-profile">
      <div className="lp-publish-row">
        {showSaveChanges && (
          <Button className="lp-save-btn" size="large" onClick={saveDraftNow} loading={savingDraft} disabled={!dirty}>
            Save Changes
          </Button>
        )}
        <Button className="lp-publish-btn" size="large" onClick={publishChanges} loading={saving} disabled={!canPublish}>
          {publishLabel}
        </Button>
      </div>

      <div className="lp-strength">
        <span className="lp-strength-label">Profile Strength</span>
        <div className="lp-strength-bar">
          <div className="lp-strength-fill" style={{ width: `${strength}%` }} />
        </div>
        <span className="lp-strength-pct">{strength}%</span>
      </div>

      {/* ---- BASICS ---- */}
      <Section label="Basics">
        <Field label="Business Name">
          <Input
            placeholder="Business Name"
            size="large"
            value={form.businessName}
            onChange={(e) => update("businessName", e.target.value)}
          />
        </Field>

        <Field label="Street Address">
          <Input
            placeholder="Address"
            size="large"
            value={form.street}
            onChange={(e) => update("street", e.target.value)}
          />
        </Field>

        <Field label="Street Address Line 2 (Optional)">
          <Input
            placeholder="Address Line 2"
            size="large"
            value={form.street2}
            onChange={(e) => update("street2", e.target.value)}
          />
        </Field>

        <div className="lp-row lp-row-3">
          <Field label="City">
            <Input placeholder="City" size="large" value={form.city} onChange={(e) => update("city", e.target.value)} />
          </Field>
          <Field label="State">
            <Input placeholder="State" size="large" value={form.state} onChange={(e) => update("state", e.target.value)} />
          </Field>
          <Field label="Zip/Postal Code">
            <Input
              placeholder="Zip/Postal Code"
              size="large"
              value={form.zip}
              onChange={(e) => update("zip", e.target.value)}
            />
          </Field>
        </div>

        <div className="lp-pin">
          <span className="lp-pin-text">
            <span className="lp-pin-icon" aria-hidden>📍</span>
            Map pin set from your address
          </span>
        </div>

        <div className="lp-row">
          <Field label="Primary Phone">
            <Input
              placeholder="Primary Phone"
              size="large"
              value={form.primaryPhone}
              onChange={(e) => update("primaryPhone", e.target.value)}
            />
          </Field>
          <Field label="Website">
            <Input
              placeholder="https://yourrestaurant.com"
              size="large"
              value={form.website}
              onChange={(e) => update("website", e.target.value)}
              onBlur={(e) => update("website", ensureHttps(e.target.value))}
            />
          </Field>
        </div>

        <button type="button" className="lp-collapse-toggle" onClick={() => setWayfindingOpen((v) => !v)}>
          Wayfinding note <span className="lp-optional">(optional)</span>
          <DownOutlined className={`lp-chevron${wayfindingOpen ? " open" : ""}`} />
        </button>
        {wayfindingOpen && (
          <TextArea
            placeholder="e.g. 2nd floor of the shopping mall, entrance on Main St."
            rows={2}
            value={form.wayfindingNote}
            onChange={(e) => update("wayfindingNote", e.target.value)}
          />
        )}
      </Section>

      {/* ---- CATEGORY ---- */}
      <Section label="Category">
        <div className="lp-cat-head">
          <span className="lp-cat-title">
            What kind of restaurant? <span className="lp-cat-count">({form.categories.length} of {MAX_CATEGORIES})</span>
          </span>
          <p className="lp-cat-sub">First = what Google ranks you for. Fewer, accurate categories beat many.</p>
        </div>

        <div className="lp-chips">
          {quickCategories.map((c) => (
            <Chip
              key={c.id}
              label={c.name}
              active={form.categories.includes(c.id)}
              disabled={!form.categories.includes(c.id) && form.categories.length >= MAX_CATEGORIES}
              onClick={() => toggleCategory(c.id)}
            />
          ))}
          {extraSelected.map((id) => (
            <Chip key={id} label={categoryById.get(id)?.name ?? id} active onClick={() => toggleCategory(id)} />
          ))}
        </div>

        <div className="lp-cat-search">
          <Input
            size="large"
            prefix={<SearchOutlined className="lp-cat-search-icon" />}
            placeholder={`Search ${catalog.length || 150}+ restaurant types`}
            value={catQuery}
            onChange={(e) => setCatQuery(e.target.value)}
          />
          {searchResults.length > 0 && (
            <div className="lp-cat-results">
              {searchResults.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  className="lp-cat-result"
                  disabled={form.categories.length >= MAX_CATEGORIES}
                  onClick={() => {
                    toggleCategory(c.id);
                    setCatQuery("");
                  }}
                >
                  {c.name}
                </button>
              ))}
            </div>
          )}
        </div>
      </Section>

      {/* ---- ACTION LINKS ---- */}
      <Section label="Action Links">
        <div className="lp-banner">
          <LinkOutlined className="lp-banner-icon" />
          Menu link — your live MHM menu page, published automatically
        </div>

        <div className="lp-row">
          <Field label="Online Ordering">
            <Input
              placeholder="https://yourrestaurant.com/order"
              size="large"
              value={form.onlineOrdering}
              onChange={(e) => update("onlineOrdering", e.target.value)}
              onBlur={(e) => update("onlineOrdering", ensureHttps(e.target.value))}
            />
          </Field>
          <Field label="Reservations">
            <Input
              placeholder="https://yourrestaurant.com/reservations"
              size="large"
              value={form.reservations}
              onChange={(e) => update("reservations", e.target.value)}
              onBlur={(e) => update("reservations", ensureHttps(e.target.value))}
            />
          </Field>
        </div>

        <Field label="Delivery">
          <Input
            placeholder="https://yourrestaurant.com/delivery"
            size="large"
            value={form.deliveryLink}
            onChange={(e) => update("deliveryLink", e.target.value)}
            onBlur={(e) => update("deliveryLink", ensureHttps(e.target.value))}
          />
        </Field>
      </Section>

      {/* ---- QUICK QUESTIONS ---- */}
      <Section label="Quick Questions">
        <YesNoRow label="Accepts reservations" value={form.acceptsReservations} onChange={(v) => update("acceptsReservations", v)} />
        <YesNoRow label="Outdoor seating" value={form.outdoorSeating} onChange={(v) => update("outdoorSeating", v)} />
        <YesNoRow label="Takeout (from your ordering link)" value={form.takeout} onChange={(v) => update("takeout", v)} />
        <YesNoRow label="Delivery" value={form.delivery} onChange={(v) => update("delivery", v)} />
        <YesNoRow label="Wheelchair accessible" value={form.wheelchair} onChange={(v) => update("wheelchair", v)} />

        <div className="lp-qq-row">
          <span className="lp-qq-label">Price range</span>
          <Segmented
            options={PRICE_OPTIONS}
            value={form.priceRange}
            onChange={(v) => update("priceRange", form.priceRange === v ? null : v)}
          />
        </div>

        <div className="lp-qq-row">
          <span className="lp-qq-label">Payment accepted</span>
          <div className="lp-segmented">
            {PAYMENT_OPTIONS.map((p) => (
              <button
                key={p}
                type="button"
                className={`lp-segment${form.paymentAccepted.includes(p) ? " active" : ""}`}
                onClick={() => toggleMulti("paymentAccepted", p)}
              >
                {p}
              </button>
            ))}
          </div>
        </div>

        <div className="lp-qq-row">
          <span className="lp-qq-label">Wifi available</span>
          <YesNoToggle value={form.wifiAvailable} onChange={(v) => update("wifiAvailable", v)} />
        </div>
        <div className="lp-qq-row">
          <span className="lp-qq-label">Alcohol served</span>
          <div className="lp-segmented">
            {ALCOHOL_OPTIONS.map((a) => (
              <button
                key={a}
                type="button"
                className={`lp-segment${form.alcoholServed.includes(a) ? " active" : ""}`}
                onClick={() => toggleMulti("alcoholServed", a)}
              >
                {a}
              </button>
            ))}
          </div>
        </div>
        <Field label="Cuisine types">
          <Input
            placeholder="e.g. Neapolitan, wood-fired"
            size="large"
            value={form.cuisineTypes}
            onChange={(e) => update("cuisineTypes", e.target.value)}
          />
        </Field>
      </Section>

      {/* ---- PHOTOS ---- */}
      <Section label="Photos">
        <div className="lp-photo-cards">
          <PhotoCard
            title="Cover Photo"
            placeholder="Cover Photo"
            photo={photos.cover}
            onReplace={() => setChoosing("COVER")}
          />
          <PhotoCard
            title="Logo"
            subtitle="From your Brand Kit"
            placeholder="logo"
            contain
            photo={photos.logo}
            onReplace={() => setChoosing("LOGO")}
          />
        </div>

        <div className="lp-gallery-head">
          <div>
            <span className="lp-photos-sub">Photo Gallery</span>
            <p className="lp-gallery-caption">Photos of your food, space, and team.</p>
          </div>
          <Button className="lp-add-photos" onClick={() => setChoosing("GALLERY")}>
            + Add Photos
          </Button>
        </div>

        {photos.gallery.length > 0 && (
          <div className="lp-photo-grid">
            {photos.gallery.map((p) => (
              <div className="lp-photo-tile" key={p.id}>
                <img src={p.imageUrl} alt={p.altText || "Gallery photo"} />
                <Tooltip title="Delete">
                  <button
                    type="button"
                    className="lp-photo-remove"
                    aria-label="Delete photo"
                    onClick={() => setPendingDelete(p)}
                  >
                    <DeleteOutlined />
                  </button>
                </Tooltip>
              </div>
            ))}
          </div>
        )}

        <p className="lp-photos-hint">
          Pick photos from your uploads or{" "}
          <a
            className="lp-inline-link"
            href={destinationFor(brandLocation)}
            onClick={(e) => {
              e.preventDefault();
              goToBrand(brandLocation);
            }}
          >
            Brand kit
          </a>
        </p>
      </Section>

      {/* ---- ABOUT ---- */}
      <Section label="About">
        <Field label="Description">
          <TextArea
            placeholder="Restaurant description"
            rows={4}
            value={form.description}
            onChange={(e) => update("description", e.target.value)}
          />
        </Field>

        <div className="lp-row">
          <Field label="Year established">
            <Input
              placeholder="e.g. 2015"
              size="large"
              value={form.yearEstablished}
              onChange={(e) => update("yearEstablished", e.target.value.replace(/[^0-9]/g, "").slice(0, 4))}
            />
          </Field>
          <Field label="Instagram">
            <Input
              placeholder="https://instagram.com/yourhandle"
              size="large"
              value={form.instagram}
              onChange={(e) => update("instagram", e.target.value)}
              onBlur={(e) => update("instagram", ensureHttps(e.target.value))}
            />
          </Field>
        </div>

        <Field label="Facebook">
          <Input
            placeholder="https://facebook.com/yourpage"
            size="large"
            value={form.facebook}
            onChange={(e) => update("facebook", e.target.value)}
            onBlur={(e) => update("facebook", ensureHttps(e.target.value))}
          />
        </Field>
      </Section>

      {/* ---- ADD TO YOUR PROFILE ---- */}
      <Section label="Add to your profile" optional>
        <div className="lp-chips">
          {EXTRA_CHIPS.filter((c) => !revealed.has(c.key)).map((c) => (
            <button key={c.key} type="button" className="lp-chip dashed" onClick={() => reveal(c.key)}>
              + {c.label}
            </button>
          ))}
        </div>

        {revealed.has("alsoKnownAs") && (
          <Field label="Also known as">
            <Input
              placeholder="Alternate names"
              size="large"
              value={form.alsoKnownAs}
              onChange={(e) => update("alsoKnownAs", e.target.value)}
            />
          </Field>
        )}
        {revealed.has("languagesSpoken") && (
          <Field label="Languages spoken">
            <Input
              placeholder="e.g. English, Spanish"
              size="large"
              value={form.languagesSpoken}
              onChange={(e) => update("languagesSpoken", e.target.value)}
            />
          </Field>
        )}
        {revealed.has("faqs") && (
          <Field label="FAQs">
            <TextArea
              placeholder="Question and answer"
              rows={3}
              value={form.faqs}
              onChange={(e) => update("faqs", e.target.value)}
            />
          </Field>
        )}
        {revealed.has("featuredMessage") && (
          <Field label="Featured message">
            <TextArea
              placeholder="A short promotional message"
              rows={2}
              value={form.featuredMessage}
              onChange={(e) => update("featuredMessage", e.target.value)}
            />
          </Field>
        )}
        {revealed.has("blackOwned") && (
          <div className="lp-qq-row">
            <span className="lp-qq-label">Black-owned business</span>
            <YesNoToggle value={form.blackOwned} onChange={(v) => update("blackOwned", v)} />
          </div>
        )}
        {revealed.has("xHandle") && (
          <Field label="X handle">
            <Input
              placeholder="@yourhandle"
              size="large"
              value={form.xHandle}
              onChange={(e) => update("xHandle", e.target.value)}
            />
          </Field>
        )}
        {revealed.has("video") && (
          <Field label="Video">
            <Input
              placeholder="https://youtube.com/watch?v=..."
              size="large"
              value={form.video}
              onChange={(e) => update("video", e.target.value)}
            />
          </Field>
        )}
      </Section>

      <ChoosePhotoModal
        open={choosing != null}
        memberId={member.internalId}
        teamId={member.teamId}
        locationId={locationId ?? 0}
        onClose={() => setChoosing(null)}
        onInsert={insertPhoto}
      />

      <DeletePhotoModal
        open={pendingDelete != null}
        onCancel={() => setPendingDelete(null)}
        onDelete={() => pendingDelete && removeGalleryPhoto(pendingDelete)}
      />
    </div>
  );
}

// ---- Presentational helpers ----

function computeStrength(form: ProfileForm, gallery: string[]): number {
  const checks = [
    !!form.businessName,
    !!form.street,
    !!form.city,
    !!form.state,
    !!form.zip,
    !!form.primaryPhone,
    !!form.website,
    form.categories.length > 0,
    !!form.description,
    !!form.onlineOrdering || !!form.reservations,
    !!form.priceRange,
    gallery.length > 0,
  ];
  const done = checks.filter(Boolean).length;
  return Math.round((done / checks.length) * 100);
}

function Section({
  label,
  optional,
  children,
}: {
  label: string;
  optional?: boolean;
  children: React.ReactNode;
}) {
  return (
    <section className="lp-section">
      <span className="lp-section-label">
        {label}
        {optional && <span className="lp-optional"> (optional)</span>}
      </span>
      <div className="lp-card">{children}</div>
    </section>
  );
}

function PhotoCard({
  title,
  subtitle,
  placeholder,
  contain,
  photo,
  onReplace,
}: {
  title: string;
  subtitle?: string;
  placeholder: string;
  contain?: boolean;
  photo?: ListingPhoto;
  onReplace: () => void;
}) {
  return (
    <div className="lp-photo-card">
      <div className={`lp-photo-preview${contain ? " contain" : ""}`}>
        {photo ? <img src={photo.imageUrl} alt={title} /> : <span className="lp-photo-placeholder">{placeholder}</span>}
      </div>
      <div className="lp-photo-card-foot">
        <div>
          <span className="lp-photo-card-title">{title}</span>
          {subtitle && <span className="lp-photo-card-sub">{subtitle}</span>}
        </div>
        <Button className="lp-photo-replace" onClick={onReplace}>
          Replace
        </Button>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="lp-field">
      <span className="lp-field-label">{label}</span>
      {children}
    </label>
  );
}

function YesNoRow({ label, value, onChange }: { label: string; value: YesNo; onChange: (v: YesNo) => void }) {
  return (
    <div className="lp-qq-row">
      <span className="lp-qq-label">{label}</span>
      <YesNoToggle value={value} onChange={onChange} />
    </div>
  );
}

function YesNoToggle({ value, onChange }: { value: YesNo; onChange: (v: YesNo) => void }) {
  return (
    <div className="lp-segmented">
      {(["yes", "no"] as const).map((v) => (
        <button
          key={v}
          type="button"
          className={`lp-segment${value === v ? " active" : ""}`}
          onClick={() => onChange(value === v ? null : v)}
        >
          {v === "yes" ? "Yes" : "No"}
        </button>
      ))}
    </div>
  );
}

function Segmented({
  options,
  value,
  onChange,
}: {
  options: { value: string; label: string }[];
  value: string | null;
  onChange: (v: string) => void;
}) {
  return (
    <div className="lp-segmented">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          className={`lp-segment${value === o.value ? " active" : ""}`}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function Chip({
  label,
  active,
  disabled,
  onClick,
}: {
  label: string;
  active: boolean;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={`lp-chip${active ? " active" : ""}`}
      disabled={disabled}
      onClick={onClick}
    >
      {label}
    </button>
  );
}
